import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck, Wifi, Sparkles, Droplets, UtensilsCrossed, Dumbbell, Bath, Waves, Bus,
  Home, Users, HeartHandshake, MapPin, Coffee, BookOpen, Shirt,
} from "lucide-react";
import { SITE, HERO_SLIDES, ROOMS, SERVICES, VALUES, STATS, MANIFESTO, MARQUEE_ITEMS, GALLERY, VIDEOS } from "@/data/site";

export const ICONS = {
  ShieldCheck, Wifi, Sparkles, Droplets, UtensilsCrossed, Dumbbell, Bath, Waves, Bus,
  Home, Users, HeartHandshake, MapPin, Coffee, BookOpen, Shirt,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULTS = {
  site: SITE,
  hero: {
    overline: "Samsun Kız Öğrenci Yurdu",
    titleLines: ["Güvenli, Konforlu", "ve *Modern*", "Bir Yaşam"],
    subtitle: "Yeni eğitim öğretim döneminde sizi güvenli, konforlu ve huzurlu bir yaşam alanına davet ediyoruz — üstelik denize sıfır bir konumda.",
    primaryCta: "Yurdumuzu Keşfedin",
    secondaryCta: "WhatsApp'tan Bilgi Al",
    slides: HERO_SLIDES,
  },
  intro: {
    overline: "Ada'ya Hoş Geldiniz",
    title: "Bir yurttan fazlası; *ikinci eviniz*",
    description: "Samsun Atakum'da, denize sıfır bir konumdayız. Okula giden toplu taşıma duraklarına birkaç adım, sahilin en güzel yerine ise yalnızca 2 dakika yürüme mesafesindeyiz. Hem derslerinize odaklanabileceğiniz hem de sosyal yaşamın keyfini çıkarabileceğiniz ayrıcalıklı bir yaşam sizi bekliyor.",
    linkText: "Hikayemizi Okuyun",
    image: "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?q=85&w=1600&auto=format&fit=crop",
    badgeValue: "Açık Büfe",
    badgeLabel: "Kahvaltı & Akşam Yemeği",
  },
  stats: STATS,
  manifesto: MANIFESTO,
  marquee: MARQUEE_ITEMS,
  roomsPreview: {
    overline: "Odalarımız",
    title: "Size uygun *yaşam alanı*",
    description: "Tek kişilikten dört kişiliğe; balkonlu ve deniz manzaralı seçenekleriyle her odada özel banyo & WC, mini buzdolabı, TV ve ayrı giyinme–çalışma odası.",
    allLabel: "Tüm Odalar",
  },
  servicesPreview: {
    overline: "Hizmetlerimiz",
    title: "Her şey *düşünüldü*",
    description: "Açık büfe kahvaltıdan fitness salonuna, denize sıfır konumdan 24 saat sıcak suya; her şey düşünüldü.",
  },
  homeVideo: { overline: "Tanıtım Filmi", title: "Yurdumuzu Yakından Keşfedin" },
  rooms: ROOMS,
  services: SERVICES,
  values: VALUES,
  about: {
    story: {
      overline: "Hikayemiz",
      title: "Kızınızı bize *emanet* edin",
      description: "Samsun Atakum'da, sahilin en güzel yerinde yüzlerce öğrenciye ev sahipliği yapıyoruz. Ailelerin en çok önem verdiği iki şeyi — güvenlik ve huzur — işimizin merkezine koyduk. Ada'da her öğrenci, kendi evindeki kadar güvende ve özgür hisseder.",
      paragraph: "Deneyimli yurt ekibimiz, akademik danışmanlarımız ve 7/24 görev başında olan güvenlik personelimizle; öğrencilerimizin yalnızca derslerine ve hayallerine odaklanmasını sağlıyoruz.",
      quote: "Ada'ya gelen her öğrenci, ailesinin gözü arkada kalmadan okur.",
    },
    images: {
      main: "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?q=85&w=1600&auto=format&fit=crop",
      students: "https://images.pexels.com/photos/7683743/pexels-photo-7683743.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    lifestyle: {
      overline: "Yaşam Kültürü",
      title: "Ders çalışmak kadar *nefes almak* da önemli",
      description: "Gün batımında sahil yürüyüşleri, teras kahvaltıları, sinema geceleri ve doğum günü sürprizleri... Ada'da sosyal yaşam, akademik yaşam kadar zengin. Çünkü bizce unutulmaz üniversite yılları, unutulmaz arkadaşlıklarla yazılır.",
      bullets: [
        "Her ay düzenlenen sosyal etkinlikler ve atölyeler",
        "Sınav dönemlerinde motivasyon ikramları",
        "Mezuniyet sonrası bile süren güçlü bir mezun ağı",
      ],
      image: "https://images.unsplash.com/photo-1519046904884-53103b34b206?q=85&w=1600&auto=format&fit=crop",
    },
  },
  included: {
    overline: "Ücrete Dahil",
    title: "Tüm odalarda *standart* olarak",
    description: "Sürpriz masraf yok. Aylık ücretinize aşağıdaki tüm hizmetler dahildir.",
    items: [
      "Her odada özel banyo & WC",
      "Mini buzdolabı ve televizyon",
      "Ayrı giyinme ve çalışma odası",
      "24 saat sıcak su",
      "Sınırsız internet",
      "Açık büfe kahvaltı & 4 çeşit akşam yemeği",
    ],
  },
  servicesBanner: {
    overline: "Yaşam & Spor",
    title: "Enerjinizi sahilde ve salonda atın",
    text: "Ders sonrası 2 dakikada sahilde yürüyüş, ardından fitness salonunda antrenman. Akşam olduğunda profesyonel aşçımızın dört çeşit yemeği sizi bekler.",
    ctaLabel: "Detaylı Bilgi Alın",
    image: "https://images.pexels.com/photos/7683743/pexels-photo-7683743.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  gallery: GALLERY,
  videos: VIDEOS,
  pages: {
    home: {
      seoTitle: "Samsun Ada Kız Öğrenci Yurdu | Atakum'da Denize Sıfır Güvenli Yaşam",
      seoDesc: "Samsun Atakum'da denize sıfır, sahilin en güzel yerinde kız öğrenci yurdu. Özel banyolu, balkonlu ve deniz manzaralı 1-4 kişilik odalar; açık büfe kahvaltı, fitness salonu ve 7/24 güvenlik.",
    },
    hakkimizda: {
      overline: "Hakkımızda",
      titleLines: ["Bir yurttan fazlası,", "*bir aile sıcaklığı*"],
      description: "Ada Kız Öğrenci Yurdu; güvenlik, konfor ve topluluk duygusunu aynı çatı altında buluşturan huzurlu bir yaşam alanıdır.",
      seoTitle: "Hakkımızda | Samsun Ada Kız Öğrenci Yurdu",
      seoDesc: "Atakum sahiline 2 dakika mesafede, güvenlik ve huzuru merkeze alan kız öğrenci yurdumuzun hikayesini ve değerlerini keşfedin.",
    },
    odalar: {
      overline: "Odalarımız",
      titleLines: ["Her ihtiyaca uygun,", "*otel konforunda* odalar"],
      description: "Tek kişilikten dört kişiliğe kadar tüm odalarımızda özel banyo & WC, mini buzdolabı, televizyon ile ayrı giyinme ve çalışma odası bulunur. Balkonlu ve deniz manzaralı seçenekleri keşfedin.",
      seoTitle: "Odalarımız | 1-4 Kişilik Balkonlu & Deniz Manzaralı Odalar",
      seoDesc: "Tek kişilikten dört kişiliğe; her odada özel banyo & WC, mini buzdolabı, televizyon, ayrı giyinme ve çalışma odası. Balkonlu ve deniz manzaralı oda seçeneklerini keşfedin.",
    },
    hizmetler: {
      overline: "Hizmetlerimiz",
      titleLines: ["Siz derslerinize odaklanın,", "*gerisini biz halledelim*"],
      description: "Profesyonel aşçımızın hazırladığı açık büfe kahvaltıdan fitness salonuna, 24 saat sıcak sudan sınırsız internete; öğrenci yaşamını kolaylaştıran her detay Ada'da standart olarak sunulur.",
      seoTitle: "Hizmetlerimiz | Açık Büfe Kahvaltı, Fitness, 7/24 Güvenlik",
      seoDesc: "Profesyonel aşçıdan açık büfe kahvaltı ve dört çeşit akşam yemeği, fitness salonu, 24 saat sıcak su, sınırsız internet ve 7/24 güvenlik.",
    },
    galeri: {
      overline: "Galeri",
      titleLines: ["Ada'yı", "*kare kare* keşfedin"],
      description: "Odalarımızdan sosyal alanlarımıza; yurdumuzun her köşesini sizin için fotoğrafladık. Daha yakından görmek isterseniz, bir kahvemizi içmeye bekleriz.",
      seoTitle: "Galeri | Samsun Ada Kız Öğrenci Yurdu",
      seoDesc: "Odalarımızdan sosyal alanlarımıza, deniz manzaralı balkonlarımızdan yemekhanemize; yurdumuzu kare kare keşfedin.",
    },
    videolar: {
      overline: "Videolar",
      titleLines: ["Ada'yı", "*izleyerek* tanıyın"],
      description: "Kelimeler yetmezse görüntüler konuşsun. Oda turlarımızdan sosyal yaşamımıza kadar her şeyi kameraya aldık.",
      seoTitle: "Videolar | Samsun Ada Kız Öğrenci Yurdu",
      seoDesc: "Oda turları ve sosyal yaşam videolarıyla Samsun Ada Kız Öğrenci Yurdu'nu yakından tanıyın.",
    },
    iletisim: {
      overline: "İletişim",
      titleLines: ["Bize ulaşın,", "*kahvenizi* ikram edelim"],
      description: "Sorularınız için formu doldurun, WhatsApp'tan yazın ya da rehberli tur için randevu alın. Genellikle aynı gün içinde dönüş yapıyoruz.",
      seoTitle: "İletişim | Samsun Ada Kız Öğrenci Yurdu — Atakum",
      seoDesc: "Körfez Mah. Atatürk Bulvarı No:73, Atakum/Samsun. Telefon: 0505 799 00 55 ve 0362 799 00 55. Rehberli tur için randevu alın.",
    },
  },
  cta: {
    overline: "Sınırlı Kontenjan",
    title: "Ada'da *yerinizi* ayırtın",
    text: "Yeni dönem kayıtlarımız başladı. Kontenjanlarımız dolmadan bize ulaşın, yurdumuzu yerinde görün.",
    primaryLabel: "WhatsApp'tan Bilgi Al",
    secondaryLabel: "İletişim Formu",
  },
  footer: {
    description: "Üniversite hayatınızı daha konforlu, güvenli ve keyifli hale getiren bir yaşam alanı. Ada'da her detay, sizin huzurunuz için tasarlandı.",
    note: "Resepsiyonumuz 7/24 açıktır. Ziyaret için randevu almanız yeterlidir.",
    bottomTagline: "Güvenli • Konforlu • Modern",
  },
};

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);

  const refresh = useCallback(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setContent(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return <ContentContext.Provider value={{ content, refresh }}>{children}</ContentContext.Provider>;
};

export const useSite = () => {
  const ctx = useContext(ContentContext);
  const c = ctx?.content || {};
  const merged = {};
  for (const key of Object.keys(DEFAULTS)) {
    merged[key] = c[key] ?? DEFAULTS[key];
  }
  merged.refresh = ctx?.refresh;
  return merged;
};

export const wa = (site, message) =>
  `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(message || site.whatsappMessage)}`;
